import React, { useState } from 'react';
import PropTypes from 'prop-types';
// Utils & misc
import { cn } from '@repo/zolintra-utils';
// Components
import { Icon } from '../Icon/Icon';
import { Tooltip } from '../Tooltip/Tooltip';
// Style
import style from './MenuEntry.module.css';

export function MenuEntry({
  label, href, icon, subNavItems, isActive, isFolded, className, Link, toggleNav, theme,
}) {
  // State
  const [isOpen, setIsOpen] = useState(false);

  const getSimpleEntryElement = () => {
    const elementClassName = cn([
      style[theme],
      style.link,
      isActive && style.active,
      isFolded && style.folded,
      className,
    ]);
    const elementChild = (
      <>
        {icon ? <Icon icon={icon} className={cn([style.summaryIcon])} /> : null}
        <span className={cn([style.summaryLabel])}>{isFolded ? '' : label}</span>
      </>
    );

    return Link
      ? React.cloneElement(
        Link.component,
        {
          [Link.navigationPropName]: href,
          children: elementChild,
          className: elementClassName,
          ...Link?.props,
        },
      )
      : (
        <a
          href={href}
          className={elementClassName}
        >
          {elementChild}
        </a>
      );
  };

  /**
   * Handle toggle on summary
   * Details element supports the toggle event
   * @param {Event} event
   * @returns {void}
   */
  const handleToggleDetails = (event) => {
    event.preventDefault();
    if (isFolded && !isOpen) {
      toggleNav();
    }
    setIsOpen(!isOpen);
  };

  return subNavItems
    ? (
      <details
        className={cn([style.wrapper, style[theme], 'ui-menu-entry-wrapper', className])}
        onToggle={handleToggleDetails}
        open={isOpen && !isFolded}
      >
        <Tooltip
          content={label}
          isTriggered={isFolded}
        >
          <summary
            className={cn([
              style.summary,
              isActive && style.active,
              isFolded && style.folded,
              'ui-menu-entry-summary',
            ])}
          >
            {icon ? <Icon icon={icon} className={cn([style.summaryIcon])} /> : null}
            <span className={cn([style.summaryLabel])}>{isFolded ? '' : label}</span>
            {isFolded ? null : <Icon className={cn([style.summaryArrow])} icon="arrowDown" /> }
          </summary>
        </Tooltip>
        <ul className={cn([style.summaryList])}>
          {subNavItems.map((navItem) => (
            <li key={navItem.href} className={cn([style.summaryListItem])}>
              {Link
                ? React.cloneElement(
                  Link.component,
                  {
                    [Link.navigationPropName]: navItem.href,
                    children: navItem.label,
                    className: cn([style.summaryLabel]),
                    ...Link?.props,
                  },
                )
                : <a href={navItem.href} className={cn([style.summaryLabel])}>{navItem.label}</a>}

            </li>
          ))}
        </ul>
      </details>
    )
    : (
      <Tooltip
        content={label}
        isTriggered={isFolded}
      >
        {getSimpleEntryElement()}
      </Tooltip>
    );
}

MenuEntry.propTypes = {
  label: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  icon: PropTypes.string,
  subNavItems: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
  })),
  isActive: PropTypes.bool,
  isFolded: PropTypes.bool,
  toggleNav: PropTypes.func,
  className: PropTypes.string,
  Link: PropTypes.shape({
    component: PropTypes.node,
    navigationPropName: PropTypes.string,
    props: PropTypes.shape({}),
  }),
  theme: PropTypes.oneOf(['light', 'dark']),
};

MenuEntry.defaultProps = {
  icon: null,
  subNavItems: null,
  isActive: false,
  isFolded: false,
  toggleNav: () => {},
  className: null,
  Link: null,
  theme: 'dark',
};

export default MenuEntry;
